import React, { useEffect, useState, useMemo, FC } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "use-debounce";
import { useMediaQuery } from "react-responsive";
import { useQueryParams } from "src/hooks/useQueryParams";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { selectorWatchlist } from "src/redux/watchlist/selector";
import { getFiltersValuesAsync } from "src/redux/filters/actions";
import { selectFiltersValues } from "src/redux/filters/selectors";
import { getWatchlistAsync, ITEMS_PER_PAGE } from "src/redux/watchlist/actions";
import {
  watchlistTrns,
  noWatchlistBannerTrns,
} from "src/page-components/cabinet/watchlist/trns";
import { InquiryCard } from "src/components/InquiryCard";
import { Filtering } from "src/components/Filtering";
import { PageWrapper } from "src/components/Layouts/PageWrapper";
import { SortingDropdownList } from "src/components/SortingDropdownList";
import { Loader } from "src/components/Loader";
import { NoItemsBanner } from "src/components/NoItemsBanner";
import { MetadataWrapper } from "src/components/MetadataWrapper";
import { FilteringButton } from "src/components/Filtering/FilteringButton";
import { MobFilteringMenu } from "src/components/Filtering/MobFilteringMenu";
import { Pagination } from "src/components/Pagination";
import { INQUIRIES_SORTING_OPTIONS } from "src/components/SortingDropdownList/constants";
import { QUERY_PARAM_KEYS } from "src/constants/queryParams";
import { PATHNAMES } from "src/constants/routes";
import { SCREEN_BREAKPOINTS } from "src/constants/screenBreakpoints";
import { scrollTop } from "src/utils/scrollTop";
import { ISelectOption } from "src/@types/form";
import { Sizes } from "src/@types/sizes";

const DEBOUNCE_DELAY_DESKTOP = 1000;
const DEBOUNCE_DELAY_MOBILE = 2500;

const Watchlist: FC = () => {
  const { t } = useTranslation();
  const [isOpenFiltersMenu, setIsOpenFiltersMenu] = useState(false);

  const isGLScreenBreakpoint = useMediaQuery({
    maxWidth: SCREEN_BREAKPOINTS.GL,
  });
  const debounceDelay = isGLScreenBreakpoint
    ? DEBOUNCE_DELAY_MOBILE
    : DEBOUNCE_DELAY_DESKTOP;

  const dispatch = useAppDispatch();

  const { queryParams, getQueryParam, setQueryParam, setMultipleQueryParams } =
    useQueryParams();

  const [debouncedQueryParams] = useDebounce(queryParams, debounceDelay);

  const filtersValues = useAppSelector(selectFiltersValues);

  useEffect(() => {
    dispatch(getFiltersValuesAsync());
  }, [dispatch]);

  const activeOption = INQUIRIES_SORTING_OPTIONS.find(
    ({ value }) => value === getQueryParam(QUERY_PARAM_KEYS.SORTING)
  );

  const setOption = ({ value }: ISelectOption) =>
    setMultipleQueryParams({
      [QUERY_PARAM_KEYS.SORTING]: value,
      [QUERY_PARAM_KEYS.PAGE]: 1,
    });

  const currentPage = Number(getQueryParam(QUERY_PARAM_KEYS.PAGE)) || 1;

  const setPage = (page: number) => {
    scrollTop();
    if (page !== currentPage) {
      setQueryParam(QUERY_PARAM_KEYS.PAGE, page.toString());
    }
  };

  const filteredQueryParams = useMemo(() => {
    const params = new URLSearchParams(debouncedQueryParams);
    params.delete(QUERY_PARAM_KEYS.IS_SHOWED_MORE);
    return params.toString();
  }, [debouncedQueryParams]);

  useEffect(() => {
    dispatch(getWatchlistAsync({ filters: filteredQueryParams }));
  }, [filteredQueryParams]);

  const watchlistResponse = useAppSelector(selectorWatchlist);
  const { isLoading, items } = watchlistResponse || {};

  const watchlistResult = items?.results || [];

  const pageCount = Math.ceil(items?.count / ITEMS_PER_PAGE);
  const isPaginated = pageCount > 1;

  return (
    <MetadataWrapper name="watchlist">
      <PageWrapper mainClassName="py-25">
        <section className="container flex flex-col gap-3 gl:gap-10">
          <h1 className="font-semibold">{t(watchlistTrns.title)}</h1>

          <div className="flex items-start gap-3">
            {!isGLScreenBreakpoint && (
              <Filtering
                filtersValues={filtersValues?.clone}
                pagePath={PATHNAMES.FAVOURITE_INQUIRIES}
              />
            )}

            {isLoading ? (
              <Loader size={Sizes.L} className="self-center" />
            ) : (
              <div className="flex flex-col gap-3 flex-1">
                <div className="flex justify-between gap-2">
                  {isGLScreenBreakpoint && (
                    <>
                      <FilteringButton setIsOpen={setIsOpenFiltersMenu} />

                      <MobFilteringMenu
                        isOpen={isOpenFiltersMenu}
                        setIsOpen={setIsOpenFiltersMenu}
                        filtersValues={filtersValues?.clone}
                        pagePath={PATHNAMES.FAVOURITE_INQUIRIES}
                      />
                    </>
                  )}
                  <SortingDropdownList
                    options={INQUIRIES_SORTING_OPTIONS}
                    activeOption={activeOption || INQUIRIES_SORTING_OPTIONS[0]}
                    setOption={setOption}
                  />
                </div>

                <div className="flex flex-col gap-3">
                  {Boolean(watchlistResult?.length) ? (
                    watchlistResult.map((inquiry) => (
                      <InquiryCard
                        key={inquiry.id}
                        inquiry={inquiry}
                        isReadOnly={true}
                      />
                    ))
                  ) : (
                    <NoItemsBanner
                      title={t(noWatchlistBannerTrns.title)}
                      description={t(noWatchlistBannerTrns.description)}
                      href={PATHNAMES.INQUIRIES}
                      textButton={t(
                        noWatchlistBannerTrns.addInquiryToWatchlistButton
                      )}
                    />
                  )}

                  {isPaginated && (
                    <>
                      <Pagination
                        className="self-center my-8"
                        page={currentPage}
                        pageCount={pageCount}
                        setPage={setPage}
                      />
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
      </PageWrapper>
    </MetadataWrapper>
  );
};

export default Watchlist;
